/**
 * @file H5 平台策略
 * @description 纯 H5 移动端浏览器环境的平台能力实现
 */

import {
  PlatformType,
  type IPlatformStrategy,
  type NavigateToOptions,
  type LoginOptions,
  type ShareData,
  type LocationResult,
  type LocationNavigateOptions
} from '../types'
import { getGeoInfo } from '@h5-journey/service'
import { setRequestFromHeader } from '../request-from'
import { BigNumber } from 'bignumber.js'
import {
  getUniqueId,
  removePubParamsOfUrl,
  appendSourceFlagToUrl,
  isInHostMiniAppWebView
} from '@h5-journey/utils'

export function createH5Strategy(): IPlatformStrategy {
  return {
    type: PlatformType.H5,

    async onInit({ userStore }) {
      // 使用应用环境配置；未配置时保留应用入口已经设置的站点来源。
      const client = import.meta.env.VITE_REQUEST_FROM_CLIENT
      const version = import.meta.env.VITE_REQUEST_FROM_VERSION
      const cuid = getUniqueId()
      if (client && version) setRequestFromHeader({ client, version, cuid })
      console.log(sessionStorage.getItem('Request-From'), 'request-from')

      // 独立 H5：从 localStorage 恢复记住登录。
      // 宿主 WebView（元服务会落到 H5 策略）不能恢复：宿主退出清不到 H5 localStorage，恢复会把旧 token 救活。
      if (!isInHostMiniAppWebView()) {
        const keys = ['memberId', 'loginToken'] as const
        keys.forEach((key) => {
          if (!sessionStorage.getItem(key)) {
            const value = localStorage.getItem(key)
            if (value) {
              sessionStorage.setItem(key, value)
            }
          }
        })
        userStore.restoreUserInfo()
      }
    },

    navigation: {
      navigateTo(url: string, options: NavigateToOptions = {}) {
        const { hasHistory = true, cb } = options
        const targetUrl = appendSourceFlagToUrl(url)

        if (hasHistory) {
          window.location.href = targetUrl
        } else {
          window.location.replace(targetUrl)
        }
        // H5 环境跳转是同步的，立即执行回调
        cb?.()
      },

      goBack() {
        window.history.back()
      },

      goHome() {
        const base = (import.meta.env.VITE_BASE_URL || '/').replace(/\/$/, '')
        window.location.href = `${base}/`
      },

      closePage() {
        window.close()
      },

      async toLogin(options: LoginOptions = {}) {
        // 与旧版 utils.toLogin 对齐：清理 URL 中的登录/环境等公共参数，避免登录回跳后残留失效 token
        const returnUrl = removePubParamsOfUrl(options.returnUrl || window.location.href)
        const loginPath = options.loginPath || '/login'
        const base = (import.meta.env.VITE_BASE_URL || '/').replace(/\/$/, '')
        window.location.href = `${base}${loginPath}?url=${encodeURIComponent(returnUrl)}`
      }
    },

    communication: {
      makePhoneCall(phoneNumber: string) {
        window.open(`tel:${phoneNumber}`)
      },

      async share(_data: ShareData) {
        // H5 浏览器环境暂不支持原生分享，可后续接入 Web Share API
        console.warn('[Platform:H5] 当前环境不支持原生分享')
      }
    },

    location: {
      // H5 定位链路：GPS → 逆地理；失败时抛出错误，由调用方自行处理 fallback
      async getLocation(): Promise<LocationResult> {
        // 1. H5原生获取经纬度
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5000
          })
        })

        const lat = position.coords.latitude
        const lng = position.coords.longitude

        // 2. 调用 service 逆地理
        const geoData = await getGeoInfo(lat, lng)
        const city = geoData?.regeocode?.addressComponent?.city

        if (!city) {
          throw new Error('[H5] 逆地理编码未返回城市信息')
        }

        return {
          city,
          latitude: lat,
          longitude: lng,
          message: 'h5 success'
        }
      },
      locationNavigate(options: LocationNavigateOptions): void {
        const { type = 'amap', coordinate = 'wgs84', name = '' } = options
        if (type === 'amap') {
          const longitude = new BigNumber(options.longitude).div(1000000).toString()
          const latitude = new BigNumber(options.latitude).div(1000000).toString()
          const url = `https://uri.amap.com/marker?position=${longitude},${latitude}&callnative=1&coordinate=${coordinate}&name=${name}`
          console.log('[H5] open url', url)
          window.open(url)
        } else {
          console.error('[Platform:H5] 不支持的地图类型', type)
        }
      }
    }
  }
}
